import type {
	QuantityJournalEntry,
	QuantityWorkState,
	SentQuantityBatch
} from "~restock/loadList/helpers/quantityJournal";
import type { ErpLoadListItemDataGrid } from "~restock/loadList/helpers/types";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	CreateErpLoadListSessionInput,
	ErpAvailabilityRefreshItem,
	ErpLoadListItemData,
	ErpLoadListQuantityData,
	ErpLoadListRouteData,
	ErpLoadListSessionStateData,
	ErpLoadListSessionStatus,
	UpdateErpLoadListQuantitiesInput,
	UpdateErpLoadListSessionNameInput,
	UpdateErpLoadListSessionRoutesInput
} from "~/gql/graphqlBase.generated";
import quantityJournal from "~restock/loadList/helpers/quantityJournal";

const { isSameCell, isSentEntry, quantityCellKey, savedQuantityOf } = quantityJournal;

const computeRoutesWithValues = (quantities?: ErpLoadListQuantityData[]): string[] => {
	if (!quantities) return [];

	const routesSet = new Set<string>();
	quantities.forEach((qty) => {
		if (qty.quantity > 0) {
			routesSet.add(qty.routeCode);
		}
	});

	return Array.from(routesSet).sort();
};

const computeTotalQuantities = (quantities?: ErpLoadListQuantityData[]): number => {
	if (!quantities) return 0;
	return quantities.reduce((sum, qty) => sum + qty.quantity, 0);
};

const computeTotalCarts = (quantities?: ErpLoadListQuantityData[]): number => {
	if (!quantities) return 0;
	const total = quantities.reduce((sum, qty) => sum + (qty.containerUtilization || 0), 0);
	return Math.round(total * 100) / 100;
};

const overlayJournal = (
	quantities: ErpLoadListQuantityData[],
	entries: QuantityJournalEntry[]
): ErpLoadListQuantityData[] => {
	const overlaid = quantities.map((saved) => {
		const edited = entries.find((entry) => isSameCell(entry, saved));
		return edited ? { ...saved, containerUtilization: edited.containerUtilization, quantity: edited.quantity } : saved;
	});
	for (const entry of entries) {
		if (quantities.some((saved) => isSameCell(saved, entry))) continue;
		overlaid.push({
			containerCode: entry.containerCode,
			containerUtilization: entry.containerUtilization,
			erpItemId: entry.erpItemId,
			quantity: entry.quantity,
			routeCode: entry.routeCode,
			updatedAt: "" as ErpLoadListQuantityData["updatedAt"],
			updatedBy: "",
			upc: entry.upc
		});
	}
	return overlaid;
};

const computeItemsWithRemaining = (
	routes: ErpLoadListRouteData[],
	quantities: ErpLoadListQuantityData[],
	rawItems: ErpLoadListItemData[]
): ErpLoadListItemDataGrid[] => {
	const exactQuantity = new Map<string, number>();
	const unlistedUpcQuantity = new Map<string, number>();
	const rowUpcs = new Set(rawItems.map((item) => `${item.erpItemId}_${item.upc}`));
	for (const qty of quantities) {
		exactQuantity.set(quantityCellKey(qty), qty.quantity);
		if (!rowUpcs.has(`${qty.erpItemId}_${qty.upc}`))
			unlistedUpcQuantity.set(`${qty.erpItemId}_${qty.routeCode}`, qty.quantity);
	}

	return rawItems.map((item) => {
		const itemRow: ErpLoadListItemDataGrid = {
			...item,
			allocated: 0,
			remainingAvailability: item.availableQuantity - item.inTransitQuantity
		};

		let totalQuantityUsed = 0;

		routes.forEach((route) => {
			const cell = { erpItemId: item.erpItemId, routeCode: route.routeCode, upc: item.upc };
			const quantity =
				exactQuantity.get(quantityCellKey(cell)) ??
				unlistedUpcQuantity.get(`${item.erpItemId}_${route.routeCode}`) ??
				0;
			itemRow[`station_${route.routeCode}`] = quantity;
			totalQuantityUsed += quantity;
		});

		itemRow.allocated = totalQuantityUsed;
		itemRow.remainingAvailability = item.availableQuantity - item.inTransitQuantity - totalQuantityUsed;

		return itemRow;
	});
};

interface LoadListState {
	availabilityItems: ErpLoadListItemData[];
	copyRouteModalOpen: boolean;
	createSessionInput?: CreateErpLoadListSessionInput;
	currentSession?: ErpLoadListSessionStateData;
	exportModalOpen: boolean;
	itemsWithUpdates: ErpLoadListItemDataGrid[];
	newSessionName: string;
	quantityGeneration: number;
	quantityJournal: QuantityJournalEntry[];
	quantitySendsInFlight: string[];
	routeStationModalOpen: boolean;
	routesWithValues: string[];
	selectedStations?: string[];
	sessionManagerModalOpen: boolean;
	stationModalOpen: boolean;
	submitOrdersModalOpen: boolean;
	totalCarts: number;
	totalQuantities: number;
	updateNameInput?: UpdateErpLoadListSessionNameInput;
	updateQuantitiesInput?: UpdateErpLoadListQuantitiesInput;
	updateRoutesInput?: UpdateErpLoadListSessionRoutesInput;
	userStation?: string;
	userStations: string[];
	userStationSelectorOpen: boolean;
	zeroOutModalOpen: boolean;
}

const initialState: LoadListState = {
	availabilityItems: [],
	copyRouteModalOpen: false,
	exportModalOpen: false,
	itemsWithUpdates: [],
	newSessionName: "",
	quantityGeneration: 0,
	quantityJournal: [],
	quantitySendsInFlight: [],
	routeStationModalOpen: false,
	routesWithValues: [],
	selectedStations: [],
	sessionManagerModalOpen: false,
	stationModalOpen: false,
	submitOrdersModalOpen: false,
	totalCarts: 0,
	totalQuantities: 0,
	userStation: "",
	userStations: [],
	userStationSelectorOpen: false,
	zeroOutModalOpen: false
};

const hydrateSession = (state: LoadListState, session: ErpLoadListSessionStateData) => {
	const entries = state.quantityJournal.filter((entry) => entry.sessionId === session.sessionId);
	const quantities = overlayJournal(session.quantities || [], entries);
	state.routesWithValues = computeRoutesWithValues(quantities);
	state.totalCarts = computeTotalCarts(quantities);
	state.totalQuantities = computeTotalQuantities(quantities);
	state.itemsWithUpdates = computeItemsWithRemaining(session.routes || [], quantities, session.items || []);
};

const settleSent = (state: LoadListState, batch: SentQuantityBatch, settled: QuantityWorkState) => {
	for (const entry of state.quantityJournal) {
		if (isSentEntry(entry, batch)) entry.state = settled;
	}
};

const endSend = (state: LoadListState, batch: SentQuantityBatch) => {
	state.quantitySendsInFlight = state.quantitySendsInFlight.filter((sessionId) => sessionId !== batch.sessionId);
};

export const loadListSlice = createSlice({
	initialState,
	name: "loadList",
	reducers: {
		acknowledgeQuantities: (state, action: PayloadAction<SentQuantityBatch>) => {
			state.quantityJournal = state.quantityJournal.filter((entry) => !isSentEntry(entry, action.payload));
			endSend(state, action.payload);
		},
		addToTotalQuantities: (state, action: PayloadAction<number>) => {
			state.totalQuantities += action.payload;
		},
		clearCurrentSession: (state) => {
			state.availabilityItems = [];
			state.currentSession = undefined;
			state.createSessionInput = undefined;
			state.itemsWithUpdates = [];
			state.updateNameInput = undefined;
			state.updateQuantitiesInput = undefined;
			state.updateRoutesInput = undefined;
			state.routesWithValues = [];
			state.quantityJournal = [];
			state.totalCarts = 0;
			state.totalQuantities = 0;
		},
		closeCopyRouteModal: (state) => {
			state.copyRouteModalOpen = false;
		},
		closeExportModal: (state) => {
			state.exportModalOpen = false;
		},
		closeRouteStationModal: (state) => {
			state.routeStationModalOpen = false;
		},
		closeSessionManagerModal: (state) => {
			state.sessionManagerModalOpen = false;
		},
		closeStationModal: (state) => {
			state.stationModalOpen = false;
		},
		closeSubmitOrdersModal: (state) => {
			state.submitOrdersModalOpen = false;
		},
		closeUserStationSelectorModal: (state) => {
			state.userStationSelectorOpen = false;
		},
		closeZeroOutModal: (state) => {
			state.zeroOutModalOpen = false;
		},
		discardFailedQuantities: (state, action: PayloadAction<string>) => {
			state.quantityJournal = state.quantityJournal.filter(
				(entry) => entry.sessionId !== action.payload || entry.state !== "failed"
			);
			if (state.currentSession?.sessionId === action.payload) hydrateSession(state, state.currentSession);
		},
		dropQuantityJournal: (state, action: PayloadAction<string>) => {
			state.quantityJournal = state.quantityJournal.filter((entry) => entry.sessionId !== action.payload);
		},
		markQuantitiesSending: (state, action: PayloadAction<SentQuantityBatch>) => {
			settleSent(state, action.payload, "sending");
			state.quantitySendsInFlight.push(action.payload.sessionId);
		},
		openCopyRouteModal: (state) => {
			state.copyRouteModalOpen = true;
		},
		openExportModal: (state) => {
			state.exportModalOpen = true;
		},
		openRouteStationModal: (state) => {
			state.routeStationModalOpen = true;
		},
		openSessionManagerModal: (state) => {
			state.sessionManagerModalOpen = true;
		},
		openStationModal: (state) => {
			state.stationModalOpen = true;
		},
		openSubmitOrdersModal: (state) => {
			state.submitOrdersModalOpen = true;
		},
		openUserStationSelectorModal: (state) => {
			state.userStationSelectorOpen = true;
		},
		openZeroOutModal: (state) => {
			state.zeroOutModalOpen = true;
		},
		reconcileQuantities: (
			state,
			action: PayloadAction<{ quantities: ErpLoadListQuantityData[]; sessionId: string }>
		) => {
			const { quantities, sessionId } = action.payload;
			state.quantityJournal = state.quantityJournal.filter(
				(entry) =>
					entry.sessionId !== sessionId ||
					entry.state !== "unknown" ||
					savedQuantityOf(quantities, entry) !== entry.quantity
			);
			for (const entry of state.quantityJournal) {
				if (entry.sessionId === sessionId && entry.state === "unknown") entry.state = "failed";
			}
		},
		retryFailedQuantities: (state, action: PayloadAction<string>) => {
			for (const entry of state.quantityJournal) {
				if (entry.sessionId === action.payload && entry.state === "failed") entry.state = "pending";
			}
		},
		setAvailabilityItems: (state, action: PayloadAction<ErpAvailabilityRefreshItem[]>) => {
			if (state.itemsWithUpdates.length === 0) return;

			const availabilityMap = new Map(action.payload.map((a) => [`${a.stationCode}_${a.erpItemId}`, a]));
			state.itemsWithUpdates.forEach((item) => {
				const match = availabilityMap.get(`${item.stationCode}_${item.erpItemId}`);
				if (match) {
					item.availableQuantity = match.quantity;
					item.inTransitQuantity = match.inTransitQty;
					item.mustGoQuantity = match.mustGoQty;
					item.remainingAvailability = match.quantity - match.inTransitQty - item.allocated;
				}
			});
		},
		setCreateSessionInput: (state, action: PayloadAction<CreateErpLoadListSessionInput | null>) => {
			if (action.payload === null) {
				state.createSessionInput = undefined;
				return;
			}
			state.createSessionInput = { ...action.payload };
		},
		setCurrentSession: (state, action: PayloadAction<ErpLoadListSessionStateData | null>) => {
			if (action.payload === null) {
				state.currentSession = undefined;
				state.itemsWithUpdates = [];
				state.routesWithValues = [];
				state.totalCarts = 0;
				state.totalQuantities = 0;
				return;
			}

			state.currentSession = { ...action.payload };
			hydrateSession(state, action.payload);
		},
		setCurrentSessionId: (state, action: PayloadAction<string>) => {
			state.currentSession = {
				sessionId: action.payload
			} as ErpLoadListSessionStateData;
			state.itemsWithUpdates = [];
			state.routesWithValues = [];
			state.totalCarts = 0;
			state.totalQuantities = 0;
		},
		setCurrentSessionStatus: (state, action: PayloadAction<ErpLoadListSessionStatus>) => {
			state.currentSession = {
				...state.currentSession,
				status: action.payload
			} as ErpLoadListSessionStateData;
		},
		setNewSessionName: (state, action: PayloadAction<string>) => {
			state.newSessionName = action.payload;
		},
		setSelectedStations: (state, action: PayloadAction<string[]>) => {
			state.selectedStations = action.payload;
		},
		setUpdateNameInput: (state, action: PayloadAction<UpdateErpLoadListSessionNameInput | null>) => {
			if (action.payload === null) {
				state.updateNameInput = undefined;
				return;
			}
			state.updateNameInput = { ...action.payload };
		},
		setUpdateQuantitiesInput: (state, action: PayloadAction<UpdateErpLoadListQuantitiesInput | null>) => {
			if (action.payload === null) {
				state.updateQuantitiesInput = undefined;
				return;
			}
			state.updateQuantitiesInput = { ...action.payload };
		},
		setUpdateRoutesInput: (state, action: PayloadAction<UpdateErpLoadListSessionRoutesInput | null>) => {
			if (action.payload === null) {
				state.updateRoutesInput = undefined;
				return;
			}
			state.updateRoutesInput = { ...action.payload };
		},
		setUserStation: (state, action: PayloadAction<string>) => {
			state.userStation = action.payload;
		},
		setUserStations: (state, action: PayloadAction<string[]>) => {
			state.userStations = action.payload;
		},
		settleQuantities: (state, action: PayloadAction<{ batch: SentQuantityBatch; state: "failed" | "unknown" }>) => {
			settleSent(state, action.payload.batch, action.payload.state);
			endSend(state, action.payload.batch);
		},
		updateCreateSessionInput: (state, action: PayloadAction<Partial<CreateErpLoadListSessionInput>>) => {
			if (!state.createSessionInput) return;
			state.createSessionInput = { ...state.createSessionInput, ...action.payload };
		},
		updateCurrentSessionItems: (state, action: PayloadAction<ErpLoadListItemData[]>) => {
			if (!state.currentSession) return;
			state.currentSession.items = action.payload;
		},
		updateCurrentSessionName: (state, action: PayloadAction<string>) => {
			if (!state.currentSession) return;
			state.currentSession.sessionName = action.payload;
		},
		updateCurrentSessionQuantities: (state, action: PayloadAction<ErpLoadListQuantityData[]>) => {
			if (!state.currentSession) return;
			state.currentSession.quantities = action.payload;
			state.routesWithValues = computeRoutesWithValues(action.payload);
		},
		updateCurrentSessionRoutes: (state, action: PayloadAction<ErpLoadListRouteData[]>) => {
			if (!state.currentSession) return;
			state.currentSession.routes = action.payload;
		},
		updateCurrentSessionStatus: (state, action: PayloadAction<ErpLoadListSessionStatus>) => {
			if (!state.currentSession) return;
			state.currentSession.status = action.payload;
		},
		updateItemQuantityAndAvailability: (
			state,
			action: PayloadAction<{
				containerCode: string;
				containerUtilization: number;
				erpItemId: string;
				newQuantity: number;
				oldQuantity: number;
				routeCode: string;
				upc: string;
			}>
		) => {
			const { containerCode, containerUtilization, erpItemId, newQuantity, oldQuantity, routeCode, upc } =
				action.payload;
			const session = state.currentSession;
			const item = state.itemsWithUpdates.find((item) => item.erpItemId === erpItemId && item.upc === upc);
			if (!item || !session) return;

			const stationField = `station_${routeCode}`;
			item[stationField] = newQuantity;

			const difference = newQuantity - oldQuantity;
			item.allocated += difference;
			item.remainingAvailability -= difference;

			if (newQuantity > 0 && !state.routesWithValues.includes(routeCode)) {
				state.routesWithValues.push(routeCode);
				state.routesWithValues.sort();
			}

			state.quantityGeneration += 1;
			const edit: QuantityJournalEntry = {
				containerCode,
				containerUtilization,
				erpItemId,
				generation: state.quantityGeneration,
				quantity: newQuantity,
				routeCode,
				sessionId: session.sessionId,
				sessionName: session.sessionName ?? "",
				state: "pending",
				upc
			};
			const existingIndex = state.quantityJournal.findIndex(
				(entry) => entry.sessionId === session.sessionId && isSameCell(entry, edit)
			);
			if (existingIndex >= 0) state.quantityJournal[existingIndex] = edit;
			else state.quantityJournal.push(edit);
		}
	}
});

export const {
	acknowledgeQuantities,
	addToTotalQuantities,
	clearCurrentSession,
	closeCopyRouteModal,
	closeExportModal,
	closeRouteStationModal,
	closeSessionManagerModal,
	closeSubmitOrdersModal,
	closeUserStationSelectorModal,
	closeZeroOutModal,
	discardFailedQuantities,
	dropQuantityJournal,
	markQuantitiesSending,
	openCopyRouteModal,
	openExportModal,
	openRouteStationModal,
	openSessionManagerModal,
	openSubmitOrdersModal,
	openUserStationSelectorModal,
	openZeroOutModal,
	reconcileQuantities,
	retryFailedQuantities,
	setAvailabilityItems,
	setCreateSessionInput,
	setCurrentSession,
	setCurrentSessionId,
	setCurrentSessionStatus,
	setNewSessionName,
	setSelectedStations,
	setUpdateNameInput,
	setUpdateQuantitiesInput,
	setUpdateRoutesInput,
	setUserStation,
	setUserStations,
	settleQuantities,
	updateCreateSessionInput,
	updateCurrentSessionItems,
	updateCurrentSessionName,
	updateCurrentSessionQuantities,
	updateCurrentSessionRoutes,
	updateCurrentSessionStatus,
	updateItemQuantityAndAvailability
} = loadListSlice.actions;

export default loadListSlice.reducer;
